import {
  Box,
  Button,
  Divider,
  HStack,
  List,
  ListItem,
  Popover,
  PopoverBody,
  PopoverContent,
  PopoverTrigger,
  Portal,
  Text,
  VStack,
} from "@chakra-ui/react";
import { LuBell } from "react-icons/lu";
import { Link as RouterLink } from "react-router";
import CDS from "design-system";
import { StakeholderItem } from "features/SendMessage/types";

interface PopoverSection {
  stakeholder: StakeholderItem;
  isCurrentUser: boolean;
}

interface OpenTasksPopoverProps {
  /** Current user's stakeholder data (shown as "My Tasks") */
  currentUserStakeholder?: StakeholderItem;
  /** Other stakeholders with open tasks */
  otherStakeholders: StakeholderItem[];
  /** My-Tasks deep-link pre-filtered to this new hire */
  resolveHref: string;
  /** Callback when remind button is clicked */
  onRemind: () => void;
  /** The trigger element */
  children: React.ReactNode;
}

function PopoverSectionContent({
  section,
  resolveHref,
  onRemind,
  isLast,
}: {
  section: PopoverSection;
  resolveHref: string;
  onRemind: () => void;
  isLast: boolean;
}) {
  const { stakeholder, isCurrentUser } = section;
  const openCount = stakeholder.overdueCount + stakeholder.urgentCount + stakeholder.activeCount;

  return (
    <Box>
      <VStack align="stretch" spacing={1} mb={2}>
        <HStack justify="space-between" align="center">
          {isCurrentUser ? (
            <Text fontWeight="semibold" fontSize="sm">
              My Tasks
            </Text>
          ) : (
            <CDS.NamePill
              name={stakeholder.user.fullName}
              avatarUrl={stakeholder.user.avatarUrl}
              showAvatar
              showClose={false}
            />
          )}

          {isCurrentUser ? (
            <Button
              as={RouterLink}
              to={resolveHref}
              size="xs"
              variant="outline"
            >
              Resolve
            </Button>
          ) : (
            <Button
              size="xs"
              variant="outline"
              leftIcon={<LuBell size={14} />}
              onClick={onRemind}
            >
              Remind
            </Button>
          )}
        </HStack>
        <CDS.TaskStatusTag.OpenTasks count={openCount} totalCount={stakeholder.totalCount} />
      </VStack>

      <List spacing={0.5} pl={2}>
        {stakeholder.tasks.map((task) => (
          <ListItem key={task.id} fontSize="sm" color="gray.600">
            <HStack spacing={2}>
              <Text color="gray.400">•</Text>
              <Text noOfLines={1} flexShrink={1} minW={0}>{task.name}</Text>
              {task.isOverdue && <CDS.TaskStatusTag.OverdueIconBadge />}
              {task.isUrgent && !task.isOverdue && <CDS.TaskStatusTag.UrgentIconBadge />}
            </HStack>
          </ListItem>
        ))}
      </List>

      {!isLast && <Divider my={3} />}
    </Box>
  );
}

export default function OpenTasksPopover({
  currentUserStakeholder,
  otherStakeholders,
  resolveHref,
  onRemind,
  children,
}: OpenTasksPopoverProps) {
  // Build sections: current user first, then others
  const sections: PopoverSection[] = [];

  if (currentUserStakeholder && currentUserStakeholder.tasks.length > 0) {
    sections.push({ stakeholder: currentUserStakeholder, isCurrentUser: true });
  }

  otherStakeholders.forEach((stakeholder) => {
    if (stakeholder.tasks.length > 0) {
      sections.push({ stakeholder, isCurrentUser: false });
    }
  });

  // If no sections, just render children without popover
  if (sections.length === 0) {

    return <>{children}</>;
  }

  return (
    <Popover trigger="hover" placement="bottom-start" openDelay={200} isLazy>
      <PopoverTrigger>
        <Box as="span" cursor="pointer">{children}</Box>
      </PopoverTrigger>
      <Portal>
        <PopoverContent w="340px" boxShadow="lg" zIndex="popover">
          <PopoverBody p={4}>
            <VStack align="stretch" spacing={0}>
              {sections.map((section, index) => (
                <PopoverSectionContent
                  key={section.stakeholder.user.id}
                  section={section}
                  resolveHref={resolveHref}
                  onRemind={onRemind}
                  isLast={index === sections.length - 1}
                />
              ))}
            </VStack>
          </PopoverBody>
        </PopoverContent>
      </Portal>
    </Popover>
  );
}
